import * as React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { customerCanJoinRemote, userSession } from 'state/session';
import Footer from 'components/Footer';
import Header from 'components/Header';
import VideoStream from 'components/VideoStream';
import Button from 'components/Button';
import { classNames } from 'utils/ui/css';
import { tenantConfig } from 'utils/tenant';
import Loader from 'components/Loader';
import { ECustomerEvents } from 'data/analytics';
import { sendCustomerAnalyticsEvent } from 'utils/ui/analytics';
import { user } from 'utils/auth/user';
import { formatDateDisplay, formatTimeDisplay } from 'utils/date';
import { integrationType, IntegrationType } from 'utils/ui/app';

export interface WaitingRoomProps {
  isDealer?: boolean;
  ready: boolean;
  setReady: (ready: boolean) => void;
  className?: string;
}

const WaitingRoom: React.FC<WaitingRoomProps> = ({ ready, setReady, className }) => {
  const session = userSession().useState();
  tenantConfig().useState();
  const { t } = useLingui();

  const isVoipCandidate = tenantConfig().isAllowed('voip') && session.sharingType == 'remote';
  const customerReady = ready || !isVoipCandidate;

  function customerReadyCb() {
    setReady(true);
    userSession().setValues({ customerReady: true });
  }

  function dealerReadyCb() {
    setReady(true);
    userSession().setValues({ customerAllowed: true });
    userSession().emit('session:dealer-waiting-room-completed', userSession().get());
  }

  React.useEffect(() => {
    if (!user.isDealer) {
      sendCustomerAnalyticsEvent(ECustomerEvents.WAITING_ROOM_OPENED, session);
    }
    // No dependencies needed since session props are not supposed to change during a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actionLabel =
    !user.isDealer && customerReady
      ? customerCanJoinRemote()
        ? t`Loading Stream...`
        : t`Waiting for Dealer...`
      : t({ context: 'brand-specific', message: 'Start Session' });

  return (
    <div
      className={classNames(
        'ext3d-waiting-room ext3d-brand-layout fixed inset-0 z-[101] flex h-screen w-screen flex-col items-stretch',
        user.isDealer && integrationType() === IntegrationType.Standalone && 'ex3d-group-ui', // For the dealer, the waiting room should be in group ui design in the standalone. For the customer and dealer view in the web components we keep the branded design.
        className
      )}
      part="waiting-room"
    >
      <Header />
      <div className="ex3d-brand-page-background flex size-full grow flex-col items-center justify-center overflow-hidden p-10">
        <h3 className="text-center text-2xl font-bold">
          {session.sharingType === 'remote' ? (
            <Trans context="brand-specific">Welcome to your 3D remote consultancy session.</Trans>
          ) : (
            <Trans>Welcome to your 3D experience.</Trans>
          )}
        </h3>
        {userSession().sessionScheduled && (
          <p className="my-2 text-center text-sm">
            <Trans>The session is scheduled to start:</Trans>{' '}
            <span>
              {formatDateDisplay(new Date(session.scheduledStartTime!), t)}{' '}
              {formatTimeDisplay(new Date(session.scheduledStartTime!))}
            </span>
            .
          </p>
        )}
        {user.isDealer ? (
          <p className="mt-2 text-center text-sm">
            <Trans>Once you are ready, the customer will be able to join the session.</Trans>
          </p>
        ) : (
          <p className="mt-2 text-center text-sm">
            {customerReady ? (
              <Trans>The session will start automatically when the salesperson joins in. Thank you for waiting.</Trans>
            ) : (
              <Trans>VOIP is enabled for this session. Please review your camera settings</Trans>
            )}
          </p>
        )}
        {session.dealerName && !user.isDealer && (
          <p className="text-center">
            <span className="text-sm font-bold">
              <Trans context="brand-specific">Brand Expert:</Trans>
            </span>
            {/* AVS-4423: a real employee's name, shown to the customer — dropped from recorder DOM snapshots. */}
            <span className="text-sm" data-e3d-mask>
              {session.dealerName}
            </span>
          </p>
        )}
        {user.isDealer && session.customerReady && (
          <p className="mt-2 text-center text-sm">
            <Trans>The customer is already ready in their waiting room.</Trans>
          </p>
        )}
        {isVoipCandidate ? (
          <VideoStream
            autoStart={true}
            containerClassName="ext3d-voip flex flex-col gap-4 w-full max-w-2xl"
            controlsClassName="flex gap-3"
            videoClassName="absolute"
            onStreamError={(error) => console.error('Stream error:', error)}
            actionElement={(streamState) => {
              const mediaResolved = streamState.initialized || !!streamState.error;
              return (
                <Button
                  icon={!user.isDealer && ready ? 'ex3d-icon--loader' : undefined}
                  onClick={user.isDealer ? dealerReadyCb : customerReadyCb}
                  className={classNames('ex3d-button--primary')}
                  disabled={user.isDealer ? !mediaResolved : ready}
                  aria-label={actionLabel}
                  text={actionLabel}
                  data-testid="waiting-room-start-session"
                />
              );
            }}
          />
        ) : (
          <Loader className="ext3d-voip" />
        )}
      </div>
      <Footer withoutWltp />
    </div>
  );
};

export default WaitingRoom;
